import config from '../config'
import { TwilioAccountListRequest } from '../types/TwilioAccount'
import { requests } from '.'
import InternalToolsService from '../../../services/InternalToolsService'

const subdomainTwilio = '/conversations/providers/twilio'

export const TwilioAccountService = {
  list: async (options: TwilioAccountListRequest) => {
    const { companyId, deleted, skip, limit, query } = { ...options }

    let urlWithParams = `${subdomainTwilio}?companyId=${companyId}${`&deleted=${deleted ?? false}`}${
      skip ? `&skip=${skip}` : ''
    }${limit ? `&limit=${limit}` : ''}${query ? `&query=${encodeURIComponent(query)}` : ''}${`&source=${
      options.source ?? ''
    }`}`

    // add all params from options.subAccountsTableFilterCheckboxes with param name being key of json
    if (options.subAccountsTableFilterCheckboxes) {
      Object.keys(options.subAccountsTableFilterCheckboxes || {}).forEach(key => {
        urlWithParams += `&${key}=${options.subAccountsTableFilterCheckboxes[key]}`
      })

      // check if all filters are false
      const allFiltersAreFalse = Object.values(options.subAccountsTableFilterCheckboxes).every(value => value === false)
      if (!allFiltersAreFalse) {
        urlWithParams += `&isSmsCapabilitiesYesChecked=true&isSmsCapabilitiesRestrictedChecked=true`
      } else {
        urlWithParams += `&isSmsCapabilitiesYesChecked=false&isSmsCapabilitiesRestrictedChecked=false`
      }
    }

    const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(urlWithParams)
    return data
  },

  getAgencyTwilio: async companyId => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
      `${subdomainTwilio}/fetch?companyId=${companyId}`
    )
    return data
  },

  getLocationTwilio: async locationId => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
      `${subdomainTwilio}/fetch?locationId=${locationId}`
    )
    return data
  },

  getMigrationCount: async (payload: any) => {
    const { companyId } = payload

    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/sub-account/migration-status-count`, {
        params: { deleted: false, companyId },
      })
      return data
    } catch (error) {
      throw error
    }
  },

  getSuspendedSubAccounts: async (payload: { companyId: string }) => {
    const { companyId } = payload
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(`/sub-account/suspended-locations`, {
        params: { companyId },
      })
      return data
    } catch (error) {
      throw error
    }
  },

  updateAgencyTwilio: async (payload: { [key: string]: any }, companyId) => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
      `${subdomainTwilio}?companyId=${companyId}`,
      payload
    )
    return data
  },

  updateSubAccountTwilio: async (
    payload: { [key: string]: any },
    locationId
  ) => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
      `${subdomainTwilio}?locationId=${locationId}`,
      payload
    )
    return data
  },

  checkAccountToken: async ({ accountSid, accountToken, companyId }) => {
    const res = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(
      `/twilio-accounts/check?accountSid=${accountSid}&accountToken=${accountToken}&companyId=${companyId}`
    )
    return res
  },

  editSubAccountTwilio: async (payload: { [key: string]: any }, locationId) => {
    const { data } = await requests(config.APP_ENGINE_API_URL).post(
      `/twilio/edit?location_id=${locationId}`,
      payload
    )
    return data
  },

  validateSubAccountSMS: async (
    payload: { [key: string]: any },
    locationId
  ) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).post(
        `/twilio/subaccount/${locationId}/validate_sms`,
        payload
      )

      return Promise.resolve(data)
    } catch (error) {
      return Promise.resolve(error)
    }
  },

  delete: async (companyId?, locationId?, close?) => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).delete(
      `${subdomainTwilio}${companyId ? `?companyId=${companyId}` : '?'}${
        locationId ? `${companyId ? '&' : ''}locationId=${locationId}` : ''
      }${close ? '' : '&dont_close_twilio=true'}`
    )
    return data
  },

  deleteIsvSubAccount: async (
    locationId: string,
    companyId: string,
    deletePhoneNumbers?: boolean,
    deleteCampaigns?: boolean,
    closeSubAccounts?: boolean
  ) => {
    const { data } = await requests(config.SAAS).delete(
      `/isv-mode/${companyId}/isv-sub-account/${locationId}?deletePhoneNumbers=${deletePhoneNumbers}&deleteCampaigns=${deleteCampaigns}&closeSubAccounts=${closeSubAccounts}`
    )
    return data
  },

  fetch: async (companyId?, locationId?) => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).get(
      `${subdomainTwilio}/fetch${companyId ? `?companyId=${companyId}` : ''}${
        locationId ? `&locationId=${locationId}` : ''
      }`
    )
    return data
  },

  createSubAccount: async locationId => {
    const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
      `${subdomainTwilio}/setup/subaccount?locationId=${locationId}`,
      {}
    )
    return data
  },

  enableISVModeForLocation: async payload => {
    const { user, locationId } = payload
    const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL, true).post(
      `/twilio-accounts/migrate-subaccount/${locationId}`,
      {
        user: {
          ...user,
          name: user.name,
        },
      }
    )
    return data
  },

  bulkEnableISVModeForLocation: async payload => {
    const { user, companyId } = payload
    const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL, true).post(
      `/twilio-accounts/migrate-subaccount-bulk/${companyId}`,
      {
        user: {
          ...user,
          name: user.name,
        },
      }
    )
    return data
  },
  enableISVModeForCompany: async (companyId, stripe, purchaseDescription) => {
    const payload = {
      entityId: companyId,
      entityType: 'COMPANY',
      amount: 1,
      currency: 'USD',
      description: purchaseDescription,
      enable3DS: true,
    }

    try {
      const { data } = await requests(config.MARKETPLACE_REST_API_URL).post(
        `/internal-tools/billing/payment-intent`,
        payload
      )

      const { paymentIntent, paymentMethod } = data

      const confirmation = await stripe.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: paymentMethod,
        }
      )

      if (!confirmation?.paymentIntent) {
        return Promise.reject(confirmation.error)
      }

      const res = await requests(config.SAAS).get(
        `/isv-mode/enable-isv-mode/${companyId}?paymentIntentId=${confirmation.paymentIntent?.id}`
      )
      return res.data
    } catch (error) {
      throw error
    }
  },

  checkRebillingPossibility: async (subAccountId, companyId) => {
    try {
      const { data } = await requests(config.APP_ENGINE_API_URL).get(
        `/twilio/check_subaccount_for_rebilling/${subAccountId}?company_id=${companyId}`
      )
      return data
    } catch (error) {
      throw error
    }
  },

  updateRebillingConfig: async (payload) => {
    try {
      const { subAccountId, rebillingConfig } = payload
      const { data } = await requests(config.SAAS).post(
        `/saas-config/${subAccountId}/update-twilio-rebilling`,
        {
          enabled: rebillingConfig.enabled,
          markup: rebillingConfig.markup,
          companyId: rebillingConfig.companyId || '',
        }
      )
      return data
    } catch (error) {
      throw error
    }
  },

  getMigratedNumbers: async (locationId: string) => {
    try {
      const { data } = await requests(config.REVEX_BACKEND_LC_PHONE_URL).get(
        `/sub-account/migrated-numbers/${locationId}`
      )
      return data
    } catch (error) {
      throw error
    }
  }
}
